// Global Instructions Rule Applied!

import { TableFilterOptionsResponse } from '../../../state_manager/rpc_models/get_table_filter_options_response.ts'
import {
  CompaniesPaginatedResponse,
  fetchCompaniesFilterOptions,
  fetchCompaniesFiltered
} from '../services/companiesService.ts'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import {
  setCompanies,
  setFilterOptions,
  setActiveFilters,
  setActiveSorting,
  clearActiveFilters,
  appendCompanies
} from '../../../state_manager/state_update_actions/companies/index.ts'
import { FilterValue, SortConfig } from '../../../state_manager/store_models/companies.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import {
  mergeUserPreferenceRowData,
  removeUserPreferenceForMenuItem
} from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  DEFAULT_COMPANIES_TABLE_FILTERS,
  areSortConfigsEqual,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow
} from '../../global_configs/tableViewPreferences.ts'
import { useReportingDefaultSorting } from '../../global_configs/useReportingDefaultSorting.ts'
import { MENU_ITEM_KEY_COMPANIES } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import { useCompaniesReportId } from '../../../state_manager/hooks/useCompaniesReportId.ts'

export const useCompaniesData = (filterOptionKeys?: string[]) => {
  const dispatch = useDispatch<AppDispatch>()

  const { menuItemId, preferenceRow, persistPreference, deletePreference } = useMenuItemUserPreference(
    MENU_ITEM_KEY_COMPANIES
  )

  const companiesReportId = useCompaniesReportId()
  const defaultTableSorting = useReportingDefaultSorting(companiesReportId)

  const [companiesDataLoading, setCompaniesDataLoading] = useState(false)
  const [companiesFilterOptionsLoading, setCompaniesFilterOptionsLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [prefsReady, setPrefsReady] = useState(false)
  const skipPersistRef = useRef(true)

  const companiesData = useSelector((state: any) => state.companies.data)
  const companiesFilterOptions = useSelector((state: any) => state.companies.filterOptions)
  const activeFilters = useSelector((state: any) => state.companies.activeFilters)
  const activeSorting = useSelector((state: any) => state.companies.activeSorting)
  const hasMore = useSelector((state: any) => state.companies.hasMore ?? false)
  const currentPage = useSelector((state: any) => state.companies.currentPage ?? 1)

  const menuItems = useSelector((state: any) => state.globalConfigs?.menu_items ?? [])

  useEffect(() => {
    if (menuItems.length === 0) return
    if (!menuItemId) {
      dispatch(setActiveFilters({ filters: DEFAULT_COMPANIES_TABLE_FILTERS }))
      dispatch(setActiveSorting({ sorting: defaultTableSorting }))
      setPrefsReady(true)
      return
    }
    const filters = resolveTableFiltersFromPreferenceRow(preferenceRow, DEFAULT_COMPANIES_TABLE_FILTERS)
    const sorting = resolveTableSortingFromPreferenceRow(preferenceRow, defaultTableSorting)
    if (!areFilterValuesEqual(filters, activeFilters)) {
      dispatch(setActiveFilters({ filters }))
    }
    if (!areSortConfigsEqual(sorting, activeSorting)) {
      dispatch(setActiveSorting({ sorting }))
    }
    setPrefsReady(true)
  }, [menuItemId, menuItems.length, preferenceRow, defaultTableSorting, dispatch])

  const handleFetchFilterOptions = useCallback(async () => {
    try {
      setCompaniesFilterOptionsLoading(true)
      const currentToken = getAccessToken(store.getState().auth)
      const keys =
        filterOptionKeys && filterOptionKeys.length > 0 ? filterOptionKeys : undefined
      const response: TableFilterOptionsResponse = await fetchCompaniesFilterOptions(currentToken, keys)
      dispatch(setFilterOptions({ response }))
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching filter options:', e)
      }
    } finally {
      setCompaniesFilterOptionsLoading(false)
    }
  }, [dispatch, filterOptionKeys?.join('|') ?? ''])

  const handleFetchCompanies = useCallback(
    async (sorting: SortConfig[] = [], filters?: FilterValue[]) => {
      try {
        setCompaniesDataLoading(true)
        const currentToken = getAccessToken(store.getState().auth)
        const response: CompaniesPaginatedResponse = await fetchCompaniesFiltered(
          sorting,
          filters ?? [],
          1,
          null,
          currentToken,
          { applyGlobalRules: false, reportId: companiesReportId }
        )
        dispatch(setCompanies({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching companies:', e)
        }
      } finally {
        setCompaniesDataLoading(false)
      }
    },
    [dispatch, companiesReportId]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      dispatch(setActiveFilters({ filters }))
    },
    [dispatch, activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      dispatch(setActiveSorting({ sorting }))
    },
    [dispatch, activeSorting]
  )

  const handleClearFilters = useCallback(() => {
    dispatch(clearActiveFilters())
  }, [dispatch])

  const handleResetFilters = useCallback(() => {
    if (!menuItemId) {
      dispatch(setActiveFilters({ filters: DEFAULT_COMPANIES_TABLE_FILTERS }))
      return
    }
    void (async () => {
      const result = await deletePreference()
      if (!result.success) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to delete companies table preferences:', result.error)
        }
        return
      }
      dispatch(removeUserPreferenceForMenuItem(menuItemId))
      skipPersistRef.current = true
      dispatch(setActiveFilters({ filters: DEFAULT_COMPANIES_TABLE_FILTERS }))
      dispatch(setActiveSorting({ sorting: defaultTableSorting }))
    })()
  }, [dispatch, menuItemId, deletePreference, defaultTableSorting])

  const persistMergedPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p) => p.menu_item_id === menuItemId)
      const row = reports.find((p) => p.menu_item_id === menuItemId)
      const merged: Record<string, unknown> = {
        ...(row?.data ?? {}),
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => {
      return persistMergedPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames })
    },
    [persistMergedPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const hadExistingRow = reports.some((p) => p.menu_item_id === menuItemId)
    const row = reports.find((p) => p.menu_item_id === menuItemId)
    const base: Record<string, unknown> = { ...(row?.data ?? {}) }
    delete base[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...base,
      [PREF_TABLE_FILTERS]: activeFilters,
      [PREF_TABLE_SORTING]: activeSorting
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      if (hadExistingRow) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
      } else {
        const user = store.getState().auth.user
        if (user) {
          await fetchGlobalConfigsThunk(user)(dispatch)
        }
      }
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  const handleClearSorting = useCallback(() => {
    dispatch(setActiveSorting({ sorting: defaultTableSorting }))
  }, [dispatch, defaultTableSorting])

  const handleLoadMore = useCallback(
    async (page: number, pageSize: number) => {
      if (loadingMore || !hasMore) return

      try {
        setLoadingMore(true)
        const currentToken = getAccessToken(store.getState().auth)
        const response = await fetchCompaniesFiltered(
          activeSorting,
          activeFilters,
          page,
          pageSize,
          currentToken,
          { applyGlobalRules: false, reportId: companiesReportId }
        )
        dispatch(appendCompanies({ response }))
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading more companies:', e)
        }
      } finally {
        setLoadingMore(false)
      }
    },
    [dispatch, activeFilters, activeSorting, hasMore, loadingMore, companiesReportId]
  )

  useEffect(() => {
    if (!prefsReady) return
    const currentToken = getAccessToken(store.getState().auth)
    if (currentToken) {
      handleFetchFilterOptions()
      handleFetchCompanies(activeSorting, activeFilters)
    } else {
      setCompaniesFilterOptionsLoading(false)
    }
  }, [prefsReady, activeFilters, activeSorting, handleFetchFilterOptions, handleFetchCompanies])

  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist companies table preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedPreference])

  return {
    companiesFilterOptions: companiesFilterOptions.data,
    companiesData,
    companiesDataLoading,
    companiesFilterOptionsLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    handleFetchCompanies,
    handleFetchFilterOptions,
    handleSetFilters,
    handleSetSorting,
    handleClearFilters,
    handleClearSorting,
    handleLoadMore,
    defaultTableFilters: DEFAULT_COMPANIES_TABLE_FILTERS,
    defaultTableSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  }
}
